import React from "react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { usePatient } from "@louise/patients/hooks/usePatient";
import {
  DiseaseHistoryProvider,
  getResumeRoute,
} from "./DiseaseHistoryContext";
import { BreastTestingScreen } from "./breast-testing/screens/BreastTestingScreen";
import { BreastTestingDetailsScreen } from "./breast-testing/screens/BreastTestingDetailsScreen";
import { BreastConditionsScreen } from "./breast-testing/screens/BreastConditionsScreen";
import { BreastSymptomOtherScreen } from "./breast-testing/screens/BreastSymptomOtherScreen";
import { DiseaseSelectionScreen } from "./diseases/screens/DiseaseSelectionScreen";
import { DiseaseDetailsScreen } from "./diseases/screens/DiseaseDetailsScreen";
import { CancerHistoryScreen } from "./cancer/screens/CancerHistoryScreen";
import { CancerDetailsScreen } from "./cancer/screens/CancerDetailsScreen";
import { CancerTreatmentsScreen } from "./cancer/screens/CancerTreatmentsScreen";
import { CancerTreatmentDetailsScreen } from "./cancer/screens/CancerTreatmentDetailsScreen";
import { DiseaseHistoryCompleteScreen } from "./screens/DiseaseHistoryCompleteScreen";
import { DISEASE_CATEGORY_ROUTES } from "./buildDiseaseHistoryNavigationStack";
import type { DiseaseCategoryRoute } from "./buildDiseaseHistoryNavigationStack";
import {
  MedicalHistoryLoadingScreen,
  MedicalHistoryQuestionProvider,
} from "@src/packages/medical-history/components";

type CategoryRouteMap = { [K in DiseaseCategoryRoute]: undefined };

export type DiseaseHistoryStackParamList = {
  BreastTesting: undefined;
  BreastTestingDetails: undefined;
  BreastConditions: undefined;
  BreastSymptomOther: undefined;
  DiseaseDetails: undefined;
  CancerHistory: undefined;
  CancerDetails: undefined;
  CancerTreatments: undefined;
  CancerTreatmentDetails: undefined;
  DiseaseHistoryComplete: undefined;
} & CategoryRouteMap;

const Stack = createNativeStackNavigator<DiseaseHistoryStackParamList>();

function DiseaseHistoryStack() {
  const { data: patient, isLoading } = usePatient();
  const initialRoute = getResumeRoute(patient);

  // Wait for patient data so initialRouteName is computed correctly
  if (isLoading && !patient) {
    return <MedicalHistoryLoadingScreen />;
  }

  return (
    <Stack.Navigator
      initialRouteName={initialRoute}
      screenOptions={{
        headerShown: false,
        contentStyle: { backgroundColor: "transparent" },
      }}
    >
      <Stack.Screen name="BreastTesting" component={BreastTestingScreen} />
      <Stack.Screen
        name="BreastTestingDetails"
        component={BreastTestingDetailsScreen}
      />
      <Stack.Screen
        name="BreastConditions"
        component={BreastConditionsScreen}
      />
      <Stack.Screen
        name="BreastSymptomOther"
        component={BreastSymptomOtherScreen}
      />
      {/* Disease category screens — one per category, same component */}
      {DISEASE_CATEGORY_ROUTES.map((routeName) => (
        <Stack.Screen
          key={routeName}
          name={routeName}
          component={DiseaseSelectionScreen}
        />
      ))}
      <Stack.Screen name="DiseaseDetails" component={DiseaseDetailsScreen} />
      <Stack.Screen name="CancerHistory" component={CancerHistoryScreen} />
      <Stack.Screen name="CancerDetails" component={CancerDetailsScreen} />
      <Stack.Screen
        name="CancerTreatments"
        component={CancerTreatmentsScreen}
      />
      <Stack.Screen
        name="CancerTreatmentDetails"
        component={CancerTreatmentDetailsScreen}
      />
      <Stack.Screen
        name="DiseaseHistoryComplete"
        component={DiseaseHistoryCompleteScreen}
      />
    </Stack.Navigator>
  );
}

export function DiseaseHistoryNavigator() {
  return (
    <DiseaseHistoryProvider>
      <MedicalHistoryQuestionProvider
        section="diseases"
        sectionLabelKey="med_history.review.section_title.diseases"
        helpTextKey="med_history.question_screen.flow_help.diseases"
      >
        <DiseaseHistoryStack />
      </MedicalHistoryQuestionProvider>
    </DiseaseHistoryProvider>
  );
}
